/**
 * client/src/lib/active-profile.ts
 *
 * Single source of truth for "which profile is the user currently acting as".
 *
 * Three surfaces need to answer that question identically — the sidebar's
 * <OrgSwitcher /> trigger + dropdown, the mobile More sheet's switcher, and
 * the persistent <ProfileContextChip /> in the app-shell header. Before this
 * module each derived its own label inline, which is how the product ended up
 * with a collapsed sidebar showing a generic building glyph for *every*
 * profile and a dropdown whose current row looked identical to the others.
 *
 * Deliberately a pure function, not a hook: the callers already hold
 * `useOrgContext()` + `useLocation()`, and keeping this dependency-free makes
 * it directly unit-testable.
 */

import { Building2, Terminal, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type ProfileKind = "personal" | "org" | "platform";

export interface ActiveProfile {
  readonly kind: ProfileKind;
  /** Human label — the org's own name when in an org context. */
  readonly label: string;
  /** Per-kind glyph, so an icon-only surface still distinguishes profiles. */
  readonly Icon: LucideIcon;
  /**
   * `@username` beside the personal label, rendered as muted secondary text so
   * the person can see WHOSE personal profile is acting. Null for org and
   * Platform Console profiles, and until the username has loaded.
   */
  readonly handle: string | null;
}

export interface ActiveProfileInputs {
  /** True while the URL is under /admin/platform* (a "both"-context route). */
  readonly onPlatform: boolean;
  readonly activeOrgId: string | null;
  readonly activeOrgName: string | null;
  /**
   * The signed-in person's username, surfaced as the personal profile's
   * `handle`. Optional: the seat-name callers (challenge workflow notices) only
   * read `label`, which stays "Personal" either way.
   */
  readonly username?: string | null;
}

/**
 * The kind→glyph mapping, exported so the switcher's dropdown rows read their
 * glyphs from here instead of re-importing the raw lucide icons. Two
 * independent mappings would drift — the trigger and the chip showing one glyph
 * while the row for the same profile shows another is the exact defect class
 * this module exists to close.
 *
 * `Terminal` for platform, NOT a shield: a shield-with-a-tick beside a profile
 * name is the one glyph a user could misread as an assurance mark, which
 * CLAUDE.md §Friction Rule clause 2 forbids implying. An operator console is
 * not an assurance about anybody.
 */
export const PROFILE_KIND_ICON: Readonly<Record<ProfileKind, LucideIcon>> = {
  personal: User,
  org: Building2,
  platform: Terminal,
};

const PERSONAL: ActiveProfile = {
  kind: "personal",
  label: "Personal",
  Icon: PROFILE_KIND_ICON.personal,
  handle: null,
};
const PLATFORM: ActiveProfile = {
  kind: "platform",
  label: "Platform Console",
  Icon: PROFILE_KIND_ICON.platform,
  handle: null,
};

/**
 * Platform Console wins over the org/personal context because /admin/platform*
 * is intentionally reachable from either one (see route-context.ts) — the URL,
 * not the stored context, is what decides which nav the user is looking at
 * (mirrors the `onPlatform` precedence in sidebar.tsx / bottom-tabs.tsx).
 */
export function resolveActiveProfile({
  onPlatform,
  activeOrgId,
  activeOrgName,
  username,
}: ActiveProfileInputs): ActiveProfile {
  if (onPlatform) return PLATFORM;
  if (!activeOrgId) {
    return username ? { ...PERSONAL, handle: `@${username}` } : PERSONAL;
  }
  return {
    kind: "org",
    // Never a bare "Org" placeholder if we can help it — the name IS the
    // signal the user is looking for. The fallback only fires in the brief
    // window where an id is known but the name has not hydrated yet.
    label: activeOrgName ?? "Organisation",
    Icon: Building2,
    handle: null,
  };
}

/** One-string form for tooltips and accessible names: "Personal (@ari_test)". */
export function profileFullName(profile: ActiveProfile): string {
  return profile.handle ? `${profile.label} (${profile.handle})` : profile.label;
}
